import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SearchIcon from '@mui/icons-material/Search';
import { PG, pgBtn } from './theme';

export type NutritionDocPage = { title: string; url: string; hint?: string };

type Props = {
  open: boolean;
  onClose: () => void;
  authHeaders: () => Promise<HeadersInit>;
  pinnedUrls: string[];
  onPin: (url: string) => void;
  onUnpin: (url: string) => void;
  /** Index endpoint. Defaults to the REST-API half of the GitBook. */
  endpoint?: string;
  blurb?: string;
  searchPlaceholder?: string;
};

export const NutritionDocsBrowser: React.FC<Props> = ({
  open,
  onClose,
  authHeaders,
  pinnedUrls,
  onPin,
  onUnpin,
  endpoint = '/api/v2/playground/nutrition/docs',
  blurb = 'Official NutritionAI docs as markdown (allowlisted). These are already searched automatically on every message — pinning is only for forcing a specific page in whole on the next send.',
  searchPlaceholder = 'Search preview vs details, affiliate, auth…',
}) => {
  const [q, setQ] = useState('');
  const [pages, setPages] = useState<NutritionDocPage[]>([]);
  const [source, setSource] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErr(null);
      try {
        const headers = await authHeaders();
        const res = await fetch(endpoint, { headers });
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(body.detail || 'Could not load GitBook index');
        if (cancelled) return;
        setSource(body.source || '');
        setPages(Array.isArray(body.pages) ? body.pages : []);
      } catch (e: any) {
        if (!cancelled) setErr(e.message || 'Could not load GitBook index');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, authHeaders, endpoint]);

  const pinned = useMemo(() => new Set(pinnedUrls), [pinnedUrls]);
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return pages;
    return pages.filter((p) => `${p.title} ${p.hint || ''} ${p.url}`.toLowerCase().includes(needle));
  }, [pages, q]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      scroll="paper"
      PaperProps={{ sx: { bgcolor: PG.panel, color: PG.text, maxHeight: '86vh' } }}
    >
      <DialogTitle sx={{ pr: 6, pb: 1 }}>
        Passio GitBook
        <Typography variant="body2" sx={{ color: PG.muted, fontWeight: 400, mt: 0.5, lineHeight: 1.45 }}>
          {blurb}
        </Typography>
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8, color: PG.muted }} aria-label="Close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <TextField
          size="small"
          fullWidth
          autoFocus
          placeholder={searchPlaceholder}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        {loading && (
          <Stack alignItems="center" sx={{ py: 6 }}>
            <CircularProgress size={28} />
          </Stack>
        )}
        {err && (
          <Typography variant="body2" sx={{ color: '#fbbf24', py: 2 }}>
            {err}
          </Typography>
        )}
        {!loading && !err && (
          <List dense disablePadding sx={{ mt: 1 }}>
            {filtered.length === 0 && (
              <Typography variant="body2" sx={{ color: PG.muted, py: 2 }}>
                No pages match.
              </Typography>
            )}
            {filtered.map((p) => {
              const isPinned = pinned.has(p.url);
              return (
                <ListItemButton key={p.url} sx={{ alignItems: 'flex-start', py: 1 }}>
                  <ListItemText
                    primary={p.title}
                    secondary={p.hint || p.url.replace('https://passio.gitbook.io/nutrition-ai/', '')}
                    primaryTypographyProps={{ fontSize: 13, fontWeight: 700 }}
                    secondaryTypographyProps={{ fontSize: 12, color: PG.muted }}
                  />
                  <Stack direction="row" spacing={0.5} alignItems="center" sx={{ ml: 1, flexShrink: 0 }}>
                    <IconButton
                      size="small"
                      component="a"
                      href={p.url.replace(/\.md$/, '')}
                      target="_blank"
                      rel="noreferrer"
                      sx={{ color: PG.muted }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                    {isPinned ? (
                      <Chip
                        size="small"
                        label="Pinned"
                        onDelete={() => onUnpin(p.url)}
                        sx={{ bgcolor: 'rgba(52,211,153,0.2)', color: PG.text }}
                      />
                    ) : (
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => onPin(p.url)}
                        sx={{ ...pgBtn, bgcolor: '#34d399', color: '#042f2e', fontWeight: 800, minHeight: 28 }}
                      >
                        Add
                      </Button>
                    )}
                  </Stack>
                </ListItemButton>
              );
            })}
          </List>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1.25, justifyContent: 'space-between' }}>
        <Typography variant="caption" sx={{ color: PG.muted, maxWidth: '70%' }} noWrap>
          {source || 'passio.gitbook.io/nutrition-ai/llms.txt'}
        </Typography>
        <Button onClick={onClose} sx={{ ...pgBtn, color: PG.text }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
